import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { getDateLocale, getDateFormats } from '@/utils/dateLocale';
import {
  Calendar as CalendarIcon,
  User,
  Users,
  Search,
  Eye,
  CheckCircle,
  XCircle,
  ChevronLeft,
  ChevronRight,
  Plus,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'wouter';

import { bookingService } from '@/services/api/booking.service';
import { useSetPageHeader } from '@/components/chrome/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { SearchBar } from '@/components/ui/search-bar';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { StatusBadge } from '@/components/ui/badge';
import { FloatingActionButton } from '@/components/ui/floating-action-button';
import { Calendar } from '@/components/calendar/Calendar';
import { Calendar as DatePicker } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { CalendarEvent } from '@/lib/calendar/types';
import { bookingsToEvents } from '@/lib/calendar/bookings';
import { useToast } from '@/hooks/use-toast';
import type { Booking } from '@/types/booking';
import { useSubscription } from '@/contexts/subscription-context';
import { LimitUsageIndicator } from '@/components/subscription/LimitUsageIndicator';
import { type BookingStatus } from '@/lib/status-colors';
import { StatusFilterChip } from '@/components/ui/status-filter-chip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/contexts/auth-context';

type TimeRange = 'upcoming' | 'past' | 'all';
type ViewMode = 'list' | 'day' | 'week' | 'month';
type SortDir = 'asc' | 'desc';
type StatusFilter = 'all' | 'pending' | 'confirmed' | 'completed' | 'cancelled' | 'no_show';

const PER_PAGE = 20;
const SEARCH_DEBOUNCE_MS = 300;
const STATUS_OPTIONS: StatusFilter[] = ['all', 'pending', 'confirmed', 'completed', 'cancelled', 'no_show'];
const TIME_RANGE_OPTIONS: TimeRange[] = ['upcoming', 'past', 'all'];
const STATUS_ORDER = ['pending', 'confirmed', 'completed', 'cancelled', 'no_show'] as const;

function getServiceMaxCapacity(booking: Booking): number {
  const svc = booking.service as any;
  const slotSvc = booking.availableSlot?.service as any;
  return svc?.max_capacity ?? svc?.maxCapacity ?? slotSvc?.max_capacity ?? slotSvc?.maxCapacity ?? 1;
}

type AdminListEntry =
  | { kind: 'individual'; booking: Booking }
  | {
      kind: 'group';
      slotId: number;
      serviceName: string;
      providerName: string;
      startTime: string;
      participantCount: number;
      maxCapacity: number;
      statusCounts: Record<string, number>;
      bookings: Booking[];
    };

function getStartTime(booking: Booking): string | undefined {
  return booking.availableSlot?.startTime || booking.start_time;
}

function statusKey(status: string): string {
  return status === 'no_show' ? 'noShow' : status;
}

export default function AdminBookingsPage() {
  const { t } = useTranslation(['bookings', 'common', 'subscription']);
  const [location, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { limits, isAtLimit } = useSubscription();
  const { user } = useAuth();

  const { data: providers = [] } = useQuery({
    queryKey: ['providers'],
    queryFn: () => bookingService.getProviders(),
  });

  // Provider filter options — the admin's own provider sorted first so "see
  // only my bookings" is one click. Replaces the removed "My Bookings" nav item.
  const sortedProviders = useMemo(() => {
    return [...providers].sort((a, b) => {
      const aMe = a.userId === user?.id ? 0 : 1;
      const bMe = b.userId === user?.id ? 0 : 1;
      return aMe !== bMe ? aMe - bMe : a.displayName.localeCompare(b.displayName);
    });
  }, [providers, user?.id]);

  useSetPageHeader(
    {
      title: t('bookings:title'),
      sub: t('bookings:subtitle'),
      actions: (
        <Link href="/admin/bookings/new" className="hidden md:inline-flex flex-shrink-0">
          <Button
            variant="primary"
            size="md"
            disabled={isAtLimit('bookingsThisMonth')}
            title={isAtLimit('bookingsThisMonth') ? t('subscription:limitReached.monthlyBookings') : ''}
            iconLeft={<Plus className="h-4 w-4" />}
          >
            <span>{t('bookings:actions.newBooking')}</span>
          </Button>
        </Link>
      ),
    },
    [t, isAtLimit],
  );

  const initialParams = useMemo(() => {
    const url = new URLSearchParams(window.location.search);
    return {
      timeRange: ((url.get('time_range') as TimeRange) || 'upcoming') as TimeRange,
      status: ((url.get('status') as StatusFilter) || 'all') as StatusFilter,
      provider: url.get('provider_id') || 'all',
      search: url.get('search') || '',
      sortDir: ((url.get('sort_dir') as SortDir) || null) as SortDir | null,
      page: parseInt(url.get('page') || '1', 10) || 1,
      view: ((url.get('view') as ViewMode) || 'list') as ViewMode,
    };
  }, []);

  const [timeRange, setTimeRange] = useState<TimeRange>(initialParams.timeRange);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(initialParams.status);
  const [providerFilter, setProviderFilter] = useState<string>(initialParams.provider);
  const [searchInput, setSearchInput] = useState(initialParams.search);
  const [debouncedSearch, setDebouncedSearch] = useState(initialParams.search);
  const [sortDir, setSortDir] = useState<SortDir | null>(initialParams.sortDir);
  const [sortDirManual, setSortDirManual] = useState<boolean>(initialParams.sortDir !== null);
  const [currentPage, setCurrentPage] = useState(initialParams.page);
  const [viewMode, setViewMode] = useState<ViewMode>(initialParams.view);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  // Debounce search input
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    if (sortDirManual) return;
    if (timeRange === 'upcoming') setSortDir('asc');
    else if (timeRange === 'past') setSortDir('desc');
    else setSortDir(null);
  }, [timeRange, sortDirManual]);

  // Mirror the filters into the query string. Only while this page is really
  // the current URL: on browser Back the location changes before the page
  // unmounts, and replacing then would overwrite the previous history entry.
  // `location` from wouter has no query string, so compare the real URL.
  useEffect(() => {
    if (window.location.pathname !== '/admin/bookings') return;
    const url = new URLSearchParams();
    if (timeRange !== 'upcoming') url.set('time_range', timeRange);
    if (statusFilter !== 'all') url.set('status', statusFilter);
    if (providerFilter !== 'all') url.set('provider_id', providerFilter);
    if (debouncedSearch) url.set('search', debouncedSearch);
    if (sortDir) url.set('sort_dir', sortDir);
    if (currentPage > 1) url.set('page', String(currentPage));
    if (viewMode !== 'list') url.set('view', viewMode);
    const qs = url.toString();
    const next = qs ? `/admin/bookings?${qs}` : '/admin/bookings';
    if (`${window.location.pathname}${window.location.search}` !== next) setLocation(next, { replace: true });
  }, [timeRange, statusFilter, providerFilter, debouncedSearch, sortDir, currentPage, viewMode, location, setLocation]);

  const { data, isLoading, isError } = useQuery({
    queryKey: [
      'admin-bookings',
      timeRange,
      statusFilter,
      providerFilter,
      debouncedSearch,
      sortDir,
      currentPage,
      viewMode,
    ],
    queryFn: () =>
      bookingService.getAdminBookings({
        // Calendar views render past + future together; only the list view honors the upcoming/past tab.
        time_range: viewMode === 'list' ? timeRange : 'all',
        status: statusFilter === 'all' ? undefined : statusFilter,
        provider_id: providerFilter === 'all' ? undefined : Number(providerFilter),
        search: debouncedSearch || undefined,
        sort_dir: sortDir ?? undefined,
        page: viewMode === 'list' ? currentPage : 1,
        per_page: viewMode === 'list' ? PER_PAGE : 200,
      }),
    placeholderData: keepPreviousData,
  });

  const bookings = data?.bookings ?? [];
  const pagination = data?.pagination;
  const statusCounts = data?.statusCounts;

  const calendarEvents: CalendarEvent[] = useMemo(() => bookingsToEvents(bookings), [bookings]);

  const listEntries = useMemo<AdminListEntry[]>(() => {
    const groups = new Map<number, Booking[]>();
    const individuals: Booking[] = [];

    for (const b of bookings) {
      const capacity = getServiceMaxCapacity(b);
      const slotId = b.availableSlotId ?? b.availableSlot?.id;
      if (capacity > 1 && slotId != null) {
        const list = groups.get(slotId) ?? [];
        list.push(b);
        groups.set(slotId, list);
      } else {
        individuals.push(b);
      }
    }

    const groupEntries: AdminListEntry[] = Array.from(groups.entries()).map(([slotId, bs]) => {
      const statusCounts: Record<string, number> = {};
      for (const b of bs) {
        statusCounts[b.status] = (statusCounts[b.status] || 0) + 1;
      }
      const first = bs[0];
      const slot = first.availableSlot as any;
      const slotCurrent = slot?.currentBookings ?? slot?.current_bookings;
      return {
        kind: 'group',
        slotId,
        serviceName: slot?.service?.name ?? first.service?.name ?? '',
        providerName: slot?.provider?.displayName ?? slot?.provider?.name ?? first.provider?.name ?? '',
        startTime: getStartTime(first) ?? '',
        participantCount: typeof slotCurrent === 'number' ? slotCurrent : bs.length,
        maxCapacity: getServiceMaxCapacity(first),
        statusCounts,
        bookings: bs,
      };
    });

    const individualEntries: AdminListEntry[] = individuals.map((booking) => ({ kind: 'individual', booking }));

    const dir: SortDir = sortDir ?? 'asc';
    return [...groupEntries, ...individualEntries].sort((a, b) => {
      const ta = a.kind === 'group' ? a.startTime : getStartTime(a.booking) ?? '';
      const tb = b.kind === 'group' ? b.startTime : getStartTime(b.booking) ?? '';
      return dir === 'asc' ? ta.localeCompare(tb) : tb.localeCompare(ta);
    });
  }, [bookings, sortDir]);

  const handleTimeRangeChange = (next: TimeRange) => {
    setTimeRange(next);
    setCurrentPage(1);
    setSortDirManual(false);
  };

  const handleStatusChange = (next: StatusFilter) => {
    setStatusFilter(next);
    setCurrentPage(1);
  };

  const handleProviderChange = (next: string) => {
    setProviderFilter(next);
    setCurrentPage(1);
  };

  const handleSortToggle = () => {
    setSortDir(prev => (prev === 'asc' ? 'desc' : prev === 'desc' ? null : 'asc'));
    setSortDirManual(true);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setStatusFilter('all');
    setProviderFilter('all');
    setSearchInput('');
    setDebouncedSearch('');
    setCurrentPage(1);
  };

  const handleView = (booking: Booking) => {
    setLocation(`/admin/bookings/${booking.publicId ?? booking.id}`);
  };

  const matchingBookings = (bookings: Booking[], query: string): Booking[] => {
    if (!query) return [];
    const lower = query.toLowerCase();
    return bookings.filter(b => {
      const name = `${b.customerFirstName || ''} ${b.customerLastName || ''}`.trim().toLowerCase();
      const email = (b.customerEmail || '').toLowerCase();
      return name.includes(lower) || email.includes(lower);
    });
  };

  const renderGroupSubtitle = (
    entry: { participantCount: number; bookings: Booking[]; serviceName: string },
    options: { showServiceWhenNoMatch?: boolean } = {}
  ) => {
    const matched = matchingBookings(entry.bookings, debouncedSearch);
    if (matched.length === 0) {
      if (!options.showServiceWhenNoMatch) return null;
      return (
        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
          {entry.serviceName}
        </p>
      );
    }
    const shown = matched.slice(0, 2);
    const others = Math.max(0, entry.participantCount - shown.length);
    return (
      <div className="text-xs space-y-0.5 mt-0.5">
        {shown.map(b => (
          <p key={b.id} className="truncate">
            <span className="font-medium text-slate-700 dark:text-slate-300">{b.customerFirstName} {b.customerLastName}</span>
            <span className="text-slate-500 dark:text-slate-400"> · {b.customerEmail}</span>
          </p>
        ))}
        {others > 0 && (
          <p className="text-slate-500 dark:text-slate-400">
            {t('bookings:list.otherParticipants', { count: others, defaultValue: '+ {{count}} other participants' })}
          </p>
        )}
      </div>
    );
  };

  const confirmBookingMutation = useMutation({
    mutationFn: (bookingId: number) => bookingService.confirmBooking(bookingId),
    onSuccess: () => {
      toast({ title: t('common:toasts.updateSuccess'), description: t('bookings:toasts.confirmSuccess') });
      queryClient.invalidateQueries({ queryKey: ['admin-bookings'] });
    },
    onError: (error: any) => {
      toast({
        title: t('common:errors.generic'),
        description: error.message || t('bookings:toasts.confirmError'),
        variant: 'destructive',
      });
    },
  });

  const cancelBookingMutation = useMutation({
    mutationFn: (bookingId: number) => bookingService.cancelBooking(bookingId, t('bookings:details.cancelledByStaff')),
    onSuccess: () => {
      toast({ title: t('common:toasts.updateSuccess'), description: t('bookings:toasts.cancelSuccess') });
      queryClient.invalidateQueries({ queryKey: ['admin-bookings'] });
    },
    onError: (error: any) => {
      toast({
        title: t('common:errors.generic'),
        description: error.message || t('bookings:toasts.cancelError'),
        variant: 'destructive',
      });
    },
  });

  const handleConfirm = (bookingId: number) => {
    if (window.confirm(t('bookings:dialogs.confirmBooking'))) {
      confirmBookingMutation.mutate(bookingId);
    }
  };

  const handleCancel = (bookingId: number) => {
    if (window.confirm(t('bookings:dialogs.cancelBooking'))) {
      cancelBookingMutation.mutate(bookingId);
    }
  };

  const SortIcon = sortDir === 'asc'
    ? <ArrowUp className="h-3.5 w-3.5" />
    : sortDir === 'desc'
    ? <ArrowDown className="h-3.5 w-3.5" />
    : <ArrowUpDown className="h-3.5 w-3.5 text-soft" />;

  const searchPlaceholder = timeRange === 'past'
    ? t('bookings:search.placeholderPast')
    : t('bookings:search.placeholderActive');

  const isFiltered = statusFilter !== 'all' || providerFilter !== 'all' || debouncedSearch !== '';

  return (
    <div className="space-y-4 sm:space-y-6">
      {limits && !limits.usage.bookingsThisMonth.unlimited &&
       (limits.usage.bookingsThisMonth.current / limits.usage.bookingsThisMonth.limit) >= 0.8 && (
        <LimitUsageIndicator
          limitName={t('subscription:limitIndicator.monthlyBookingLimit')}
          current={limits.usage.bookingsThisMonth.current}
          max={limits.usage.bookingsThisMonth.limit}
          isUnlimited={limits.usage.bookingsThisMonth.unlimited}
          entityName={t('subscription:limitIndicator.bookingsThisMonth')}
        />
      )}

      {/* Toolbar */}
      <div className="rounded-xl border border-line bg-surface p-4 space-y-4">
        {(() => {
          const timeRangeTabs = (
            <Tabs value={timeRange} onValueChange={(v) => handleTimeRangeChange(v as TimeRange)}>
              <TabsList aria-label={t('bookings:timeRange.ariaLabel')}>
                {TIME_RANGE_OPTIONS.map((option) => (
                  <TabsTrigger key={option} value={option}>
                    {t(`bookings:timeRange.${option}`)}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          );

          const dateAndViewTabs = (
            <div className="flex items-center gap-2">
              <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label={t('bookings:actions.goToDate')}
                    title={t('bookings:actions.goToDate')}
                  >
                    <CalendarIcon className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <DatePicker
                    mode="single"
                    selected={currentDate}
                    onSelect={(d) => {
                      if (d) {
                        setCurrentDate(d);
                        if (viewMode === 'list') setViewMode('day');
                        setIsCalendarOpen(false);
                      }
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as ViewMode)}>
                <TabsList>
                  <TabsTrigger value="list">{t('bookings:viewModes.list')}</TabsTrigger>
                  <TabsTrigger value="day">{t('bookings:viewModes.day')}</TabsTrigger>
                  <TabsTrigger value="week">{t('bookings:viewModes.week')}</TabsTrigger>
                  <TabsTrigger value="month">{t('bookings:viewModes.month')}</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          );

          const searchBar = (
            <SearchBar
              containerClassName="max-w-md"
              placeholder={searchPlaceholder}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label={searchPlaceholder}
              data-testid="search-bookings"
            />
          );

          const providerSelect = sortedProviders.length > 0 ? (
            <Select value={providerFilter} onValueChange={handleProviderChange}>
              <SelectTrigger
                className="w-full sm:w-[200px] h-9 text-sm flex-shrink-0"
                aria-label={t('bookings:filter.provider')}
                data-testid="filter-provider"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('bookings:filter.allProviders')}</SelectItem>
                {sortedProviders.map((p) => (
                  <SelectItem key={p.id} value={String(p.id)}>
                    {p.userId === user?.id
                      ? t('bookings:filter.providerYou', { name: p.displayName, defaultValue: '{{name}} (You)' })
                      : p.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null;

          const statusChips = (
            <div
              role="group"
              aria-label={t('bookings:filter.status')}
              className="-mx-1 px-1 overflow-x-auto"
            >
              <div className="inline-flex items-center gap-2 min-w-min py-0.5">
                {STATUS_OPTIONS.map((status) => {
                  const active = statusFilter === status;
                  const count = status === 'all'
                    ? statusCounts?.all ?? 0
                    : statusCounts?.[status] ?? 0;
                  const label = status === 'all'
                    ? t('bookings:filter.allStatus')
                    : t(`bookings:status.${statusKey(status)}`);
                  return (
                    <StatusFilterChip
                      key={status}
                      status={status === 'all' ? 'all' : (status as BookingStatus)}
                      active={active}
                      label={label}
                      count={count}
                      onClick={() => handleStatusChange(status)}
                    />
                  );
                })}
              </div>
            </div>
          );

          return (
            <>
              {/* Desktop layout */}
              <div className="hidden sm:flex sm:flex-row sm:items-center sm:justify-between gap-3">
                {timeRangeTabs}
                {dateAndViewTabs}
              </div>
              <div className="hidden sm:flex sm:items-center sm:gap-3">
                {searchBar}
                {providerSelect}
              </div>
              <div className="hidden sm:block">{statusChips}</div>

              {/* Mobile layout: 1) timeRange  2) search + provider + chips  3) date + view tabs */}
              <div className="sm:hidden space-y-4">
                {timeRangeTabs}
                {searchBar}
                {providerSelect}
                {statusChips}
                {dateAndViewTabs}
              </div>
            </>
          );
        })()}
      </div>

      {/* List view */}
      {viewMode === 'list' && (
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            <div className="md:overflow-x-auto overflow-x-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-line bg-surface-2/60">
                    <th scope="col" className="px-4 py-3 text-left font-semibold text-soft whitespace-nowrap">
                      {t('bookings:table.customer')}
                    </th>
                    <th scope="col" className="px-4 py-3 text-left font-semibold text-soft whitespace-nowrap hidden md:table-cell">
                      {t('bookings:table.status')}
                    </th>
                    <th scope="col" className="px-4 py-3 text-left font-semibold text-soft whitespace-nowrap hidden md:table-cell">
                      {t('bookings:table.participants', 'Participants')}
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left font-semibold text-soft whitespace-nowrap hidden md:table-cell"
                      aria-sort={sortDir === 'asc' ? 'ascending' : sortDir === 'desc' ? 'descending' : 'none'}
                    >
                      <button
                        type="button"
                        onClick={handleSortToggle}
                        className="inline-flex items-center gap-1.5 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded"
                      >
                        {t('bookings:sort.date')}
                        {SortIcon}
                      </button>
                    </th>
                    <th scope="col" className="px-4 py-3 text-left font-semibold text-soft whitespace-nowrap hidden lg:table-cell">
                      {t('bookings:table.service')}
                    </th>
                    <th scope="col" className="px-4 py-3 text-left font-semibold text-soft whitespace-nowrap hidden xl:table-cell">
                      {t('bookings:table.provider')}
                    </th>
                    <th scope="col" className="px-4 py-3 text-right">
                      <span className="sr-only">{t('bookings:actions.title')}</span>
                    </th>
                  </tr>
                </thead>

                {isLoading && (
                  <tbody data-testid="bookings-loading">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <tr key={i} className="border-b border-line">
                        <td className="px-4 py-3">
                          <div className="h-4 w-32 bg-surface-2 rounded animate-pulse" />
                          <div className="h-3 w-40 bg-surface-2/60 rounded animate-pulse mt-1.5" />
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <div className="h-5 w-20 bg-surface-2 rounded animate-pulse" />
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <div className="h-4 w-12 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
                        </td>
                        <td className="px-4 py-3">
                          <div className="h-4 w-28 bg-surface-2 rounded animate-pulse" />
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <div className="h-4 w-32 bg-surface-2 rounded animate-pulse" />
                        </td>
                        <td className="px-4 py-3 hidden xl:table-cell">
                          <div className="h-4 w-24 bg-surface-2 rounded animate-pulse" />
                        </td>
                        <td className="px-4 py-3" />
                      </tr>
                    ))}
                  </tbody>
                )}

                {!isLoading && (isError || bookings.length === 0) && (
                  <tbody data-testid="bookings-empty">
                    <tr>
                      <td colSpan={6} className="px-4 py-16 text-center">
                        <CalendarIcon className="h-10 w-10 mx-auto mb-3 text-line-strong" />
                        <p className="text-soft font-medium">
                          {isError
                            ? t('bookings:list.loadError')
                            : isFiltered
                            ? (timeRange === 'past' && debouncedSearch
                                ? t('bookings:empty.noPastForQuery', { query: debouncedSearch })
                                : t('bookings:empty.noResultsFiltered'))
                            : timeRange === 'past'
                            ? t('bookings:empty.noPast')
                            : t('bookings:empty.noUpcoming')}
                        </p>
                        {!isError && isFiltered && (
                          <button
                            type="button"
                            onClick={handleClearFilters}
                            className="mt-3 text-body-sm text-brand-500 hover:underline focus-visible:outline-none focus-visible:underline"
                          >
                            {t('bookings:empty.clearFilters')}
                          </button>
                        )}
                        {!isError && !isFiltered && timeRange === 'upcoming' && (
                          <Link href="/admin/bookings/new" className="inline-block mt-4">
                            <Button variant="primary" disabled={isAtLimit('bookingsThisMonth')}>
                              <Plus className="h-4 w-4 mr-2" />
                              {t('bookings:actions.newBooking')}
                            </Button>
                          </Link>
                        )}
                      </td>
                    </tr>
                  </tbody>
                )}

                {!isLoading && !isError && bookings.length > 0 && (
                  <tbody data-testid="bookings-results">
                    {listEntries.map((entry) => entry.kind === 'group' ? (
                      <tr
                        key={`group-${entry.slotId}`}
                        onClick={() => setLocation(`/admin/group-classes/${entry.slotId}`)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setLocation(`/admin/group-classes/${entry.slotId}`);
                          }
                        }}
                        tabIndex={0}
                        role="link"
                        className="border-b border-slate-100 dark:border-slate-800 last:border-0 hover:bg-violet-50 dark:hover:bg-violet-900/20 cursor-pointer transition-colors focus:outline-none focus:bg-violet-50 dark:focus:bg-violet-900/20"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="flex-shrink-0 h-9 w-9 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                              <Users className="h-4 w-4 text-violet-600 dark:text-violet-400" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-slate-900 dark:text-white truncate">
                                {t('bookings:list.groupBooking', 'Group Booking')}
                              </p>
                              {renderGroupSubtitle(entry, { showServiceWhenNoMatch: true })}
                              <div className="md:hidden mt-1 flex flex-wrap gap-1">
                                {STATUS_ORDER.map((status) => {
                                  const count = entry.statusCounts[status] || 0;
                                  if (count === 0) return null;
                                  return (
                                    <StatusBadge key={status} status={status as BookingStatus} size="sm">
                                      {count} {t(`bookings:status.${statusKey(status)}`)}
                                    </StatusBadge>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <div className="flex flex-wrap gap-1">
                            {STATUS_ORDER.map((status) => {
                              const count = entry.statusCounts[status] || 0;
                              if (count === 0) return null;
                              return (
                                <StatusBadge key={status} status={status as BookingStatus} size="sm">
                                  {count} {t(`bookings:status.${statusKey(status)}`)}
                                </StatusBadge>
                              );
                            })}
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell whitespace-nowrap">
                          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 dark:text-slate-300">
                            <Users className="h-3.5 w-3.5 text-slate-400" />
                            <span className="tabular-nums">{entry.participantCount}/{entry.maxCapacity}</span>
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-700 dark:text-slate-300 whitespace-nowrap">
                          {entry.startTime ? (
                            <div className="flex flex-col">
                              <span className="font-medium text-slate-900 dark:text-white">
                                {format(parseISO(entry.startTime), getDateFormats().medium, { locale: getDateLocale() })}
                              </span>
                              <span className="text-xs text-slate-500 dark:text-slate-400">
                                {format(parseISO(entry.startTime), 'HH:mm')}
                              </span>
                            </div>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-700 dark:text-slate-300 hidden lg:table-cell">
                          {entry.serviceName || <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-4 py-3 text-slate-700 dark:text-slate-300 hidden xl:table-cell">
                          {entry.providerName || <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <span
                            className="inline-flex items-center justify-end text-violet-700 dark:text-violet-300"
                            aria-label={t('bookings:list.viewClass', 'View class')}
                            title={t('bookings:list.viewClass', 'View class')}
                          >
                            <ChevronRight className="h-5 w-5" />
                          </span>
                        </td>
                      </tr>
                    ) : (() => {
                      const booking = entry.booking;
                      const linkId = booking.publicId ?? booking.id;
                      const startTime = getStartTime(booking);
                      return (
                        <tr
                          key={booking.id}
                          onClick={() => setLocation(`/admin/bookings/${linkId}`)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setLocation(`/admin/bookings/${linkId}`);
                            }
                          }}
                          tabIndex={0}
                          role="link"
                          aria-label={t('bookings:actions.viewBookingAriaLabel', {
                            name: `${booking.customerFirstName} ${booking.customerLastName}`,
                          })}
                          className="border-b border-line last:border-0 hover:bg-surface-2/60 cursor-pointer transition-colors focus-visible:outline-none focus-visible:bg-surface-2/60"
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="min-w-0">
                                {startTime && (
                                  <p className="md:hidden text-body-xs font-medium text-soft mb-0.5" data-numeric>
                                    {format(parseISO(startTime), getDateFormats().medium, { locale: getDateLocale() })}
                                    <span className="ml-1.5 text-soft/70">
                                      {format(parseISO(startTime), 'HH:mm')}
                                    </span>
                                  </p>
                                )}
                                <p className="font-medium text-ink truncate">
                                  {booking.customerFirstName} {booking.customerLastName}
                                </p>
                                <p className="text-body-xs text-soft truncate">
                                  {booking.customerEmail}
                                </p>
                                <div className="md:hidden mt-1">
                                  <StatusBadge status={booking.status as BookingStatus} size="sm">
                                    {t(`bookings:status.${statusKey(booking.status)}`)}
                                  </StatusBadge>
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell">
                            <StatusBadge status={booking.status as BookingStatus}>
                              {t(`bookings:status.${statusKey(booking.status)}`)}
                            </StatusBadge>
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell whitespace-nowrap">
                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300 tabular-nums">1</span>
                          </td>
                          <td className="px-4 py-3 text-ink whitespace-nowrap hidden md:table-cell">
                            {startTime ? (
                              <div className="flex flex-col">
                                <span className="font-medium text-ink" data-numeric>
                                  {format(parseISO(startTime), getDateFormats().medium, { locale: getDateLocale() })}
                                </span>
                                <span className="text-body-xs text-soft" data-numeric>
                                  {format(parseISO(startTime), 'HH:mm')}
                                </span>
                              </div>
                            ) : (
                              <span className="text-soft">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-ink hidden lg:table-cell">
                            {booking.availableSlot?.service?.name || booking.service?.name || (
                              <span className="text-soft">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-ink hidden xl:table-cell">
                            {(booking.availableSlot?.provider as any)?.displayName || booking.provider?.name || (
                              <span className="text-soft">—</span>
                            )}
                          </td>
                          <td
                            className="px-4 py-3 text-right whitespace-nowrap"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-center justify-end gap-1.5">
                              {booking.status === 'pending' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-8 px-2 text-success border-success/40 hover:bg-success-soft"
                                  onClick={() => handleConfirm(booking.id)}
                                  data-testid={`confirm-booking-${booking.id}`}
                                  aria-label={t('bookings:actions.confirmBooking')}
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </Button>
                              )}
                              {['pending', 'confirmed'].includes(booking.status) && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-8 px-2 text-danger border-danger/40 hover:bg-danger-soft"
                                  onClick={() => handleCancel(booking.id)}
                                  data-testid={`cancel-booking-${booking.id}`}
                                  aria-label={t('bookings:actions.cancelBooking')}
                                >
                                  <XCircle className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 px-2"
                                onClick={() => handleView(booking)}
                                data-testid={`view-booking-${booking.id}`}
                                aria-label={t('bookings:actions.view')}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })())}
                  </tbody>
                )}
              </table>
            </div>

            {pagination && pagination.total_pages > 1 && (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 sm:px-6 py-4 border-t border-line">
                <div className="text-body-sm text-soft" data-numeric>
                  {t('bookings:pagination.showing', {
                    start: (pagination.page - 1) * PER_PAGE + 1,
                    end: Math.min(pagination.page * PER_PAGE, pagination.total_count),
                    total: pagination.total_count,
                  })}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={pagination.page === 1}
                    aria-label={t('bookings:pagination.previous')}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                      let pageNum: number;
                      if (pagination.total_pages <= 5) {
                        pageNum = i + 1;
                      } else if (pagination.page <= 3) {
                        pageNum = i + 1;
                      } else if (pagination.page >= pagination.total_pages - 2) {
                        pageNum = pagination.total_pages - 4 + i;
                      } else {
                        pageNum = pagination.page - 2 + i;
                      }
                      return (
                        <Button
                          key={pageNum}
                          variant={pagination.page === pageNum ? 'primary' : 'outline'}
                          size="sm"
                          onClick={() => setCurrentPage(pageNum)}
                          className="min-w-[44px]"
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.min(pagination.total_pages, p + 1))}
                    disabled={pagination.page === pagination.total_pages}
                    aria-label={t('bookings:pagination.next')}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Day / Week / Month — unified Calendar */}
      {viewMode !== 'list' && (
        <Card className="overflow-hidden">
          <Calendar
            view={viewMode}
            date={currentDate}
            onDateChange={setCurrentDate}
            events={calendarEvents}
            hideViewTabs
            loading={isLoading}
            onEventClick={(e) => {
              const m = e.meta as { booking?: Booking; groupSlotId?: number; kind?: string } | undefined;
              if (m?.kind === 'group-class' && m.groupSlotId != null) {
                setLocation(`/admin/group-classes/${m.groupSlotId}`);
              } else if (m?.booking) {
                handleView(m.booking);
              }
            }}
          />
        </Card>
      )}

      <FloatingActionButton
        icon={<Plus className="w-5 h-5" />}
        label={t('bookings:actions.newBooking')}
        onClick={() => setLocation('/admin/bookings/new')}
        disabled={isAtLimit('bookingsThisMonth')}
      />
    </div>
  );
}
